import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AlertController, ModalController } from '@ionic/angular';
import * as dayjs from 'dayjs';
import { ApiService } from './api.service';
import { GlobalService } from './global.service';
import { TaskStatus } from '../components/tasks/TaskStatus';
import { ManageComponent as TagsManageComponent } from '../components/tags/manage/manage.component';

// Tag table holding the firm's customizable "Communication Type" options (Phone Call, Email, Walk-in...)
export const TODO_COMMUNICATION_TYPE_TAGS = 'todo_communication_type';

// Set to true to keep To Do items in this browser's localStorage instead of the API (offline testing).
// The API (todos endpoints + tags/todo_communication_type) is live, so this is off.
const USE_LOCAL_STORAGE = false;
const LOCAL_TODOS_KEY = 'local_todos';
const LOCAL_COMMUNICATION_TYPES_KEY = 'local_todo_communication_types';
const DEFAULT_COMMUNICATION_TYPES = [
  { title: 'Phone Call', colour: '#90caf9' },
  { title: 'Email', colour: '#a5d6a7' },
  { title: 'Walk-in', colour: '#ffcc80' },
  { title: 'Voicemail', colour: '#ce93d8' },
  { title: 'Text Message', colour: '#80cbc4' },
];

export type TodoView = 'mine' | 'created' | 'all' | 'others';
export type TodoStatusFilter = 'open' | 'completed' | 'overdue' | 'all';

/**
 * To Do items are stand-alone internal tasks (no project, never shown in the portal).
 * They live in their own todos tables behind the /todos endpoints; communication types are
 * managed through the shared tags endpoints (tags/todo_communication_type).
 */
@Injectable({
  providedIn: 'root'
})
export class TodosService {

  readonly is_local = USE_LOCAL_STORAGE;
  private team_members: any[] = null;
  // Emits after any create/update/complete/delete so the nav bar badge can refresh
  readonly changed = new Subject<void>();

  constructor(private apiService: ApiService, private globalService: GlobalService, private alertController: AlertController, private modalController: ModalController) { }

  NewTodo(): any {
    const user = this.globalService.GetCurrentUser();
    return {
      guid: '',
      title: '',
      description: '',
      received_date: dayjs().toISOString(),
      communication_types: [],
      checklist: [],
      received_by_guid: user?.guid || '',
      client_guid: '',
      company_guid: '',
      linked_name: '',
      assigned_member_guid: '',
      due_date: dayjs().add(1, 'hour').startOf('hour').toISOString(),
      status: TaskStatus.active,
      completed_date: null,
      completed_by_guid: null,
    };
  }

  IsCompleted(todo: any): boolean {
    return todo?.status == TaskStatus.completed;
  }

  IsOverdue(todo: any): boolean {
    return !this.IsCompleted(todo) && !!todo?.due_date && dayjs(todo.due_date).isBefore(dayjs());
  }

  IsDueToday(todo: any): boolean {
    return !this.IsCompleted(todo) && !!todo?.due_date && dayjs(todo.due_date).isSame(dayjs(), 'day');
  }

  CanDelete(todo: any): boolean {
    const user = this.globalService.GetCurrentUser();
    return !!user && (user.is_admin || todo.received_by_guid === user.guid);
  }

  async GetTodos(view: TodoView, status: TodoStatusFilter, completed_days = 1, member_guid = '', client_guid = '', company_guid = '') {
    const user = this.globalService.GetCurrentUser();
    const assigned_to = view === 'mine' ? user?.guid : (view === 'others' ? member_guid : '');
    const received_by = view === 'created' ? user?.guid : '';
    // Open items are always returned in full; completed items are limited to a recent window
    const completed_after = dayjs().subtract(completed_days - 1, 'day').startOf('day').toISOString();

    if (this.is_local) {
      const todos = this.ReadLocalTodos().filter(t => {
        if (assigned_to && t.assigned_member_guid !== assigned_to) return false;
        if (received_by && t.received_by_guid !== received_by) return false;
        if (client_guid && t.client_guid !== client_guid) return false;
        if (company_guid && t.company_guid !== company_guid) return false;
        const completed = this.IsCompleted(t);
        if ((status === 'open' || status === 'overdue') && completed) return false;
        if (status === 'overdue' && !this.IsOverdue(t)) return false;
        if (status === 'completed' && !completed) return false;
        if (completed && dayjs(t.completed_date).isBefore(completed_after)) return false;
        return true;
      });
      return { success: true, data: todos };
    }

    const params: string[] = [];
    if (assigned_to) params.push(`assigned_to=${assigned_to}`);
    if (received_by) params.push(`received_by=${received_by}`);
    if (client_guid) params.push(`client=${client_guid}`);
    if (company_guid) params.push(`company=${company_guid}`);
    if (status === 'overdue') params.push('overdue=1');
    else if (status === 'open') params.push('completed=0');
    else if (status === 'completed') params.push('completed=1');
    if (status === 'completed' || status === 'all') params.push(`completed_after=${completed_after}`);

    return this.NormalizeResponse(await this.apiService.get(`todos?${params.join('&')}`));
  }

  async GetTodo(guid: string) {
    if (this.is_local) {
      const todo = this.ReadLocalTodos().find(t => t.guid === guid);
      return todo ? { success: true, data: [todo] } : { success: false, message: 'To Do not found' };
    }
    return this.NormalizeResponse(await this.apiService.get(`todos/${guid}`));
  }

  async CreateTodo(todo: any) {
    const body = this.ToPayload(todo);

    if (this.is_local) {
      const created = { ...body, guid: this.NewLocalGuid(), created_date: dayjs().toISOString() };
      this.WriteLocalTodos([...this.ReadLocalTodos(), created]);
      return { success: true, data: [created] };
    }
    return this.EmitChanged(await this.apiService.post('todos', body));
  }

  async UpdateTodo(todo: any, previous_assigned_guid: string) {
    if (this.is_local) return this.UpdateLocalTodo(todo.guid, this.ToPayload(todo));

    // The API notifies the new assignee itself when assigned_member_guid changes
    return this.EmitChanged(await this.apiService.put(`todos/${todo.guid}`, this.ToPayload(todo)));
  }

  // Changes only who the To Do is assigned to (the API notifies the new assignee)
  async AssignTodo(todo: any, member_guid: string) {
    if (this.is_local) return this.UpdateLocalTodo(todo.guid, { assigned_member_guid: member_guid || null });
    return this.EmitChanged(await this.apiService.put(`todos/${todo.guid}`, { assigned_member_guid: member_guid || null }));
  }

  // Changes only which team member received the To Do (null clears it)
  async SetReceivedBy(todo: any, member_guid: string) {
    if (this.is_local) return this.UpdateLocalTodo(todo.guid, { received_by_guid: member_guid || null });
    return this.EmitChanged(await this.apiService.put(`todos/${todo.guid}`, { received_by_guid: member_guid || null }));
  }

  // Changes only the due date (null clears it)
  async SetDueDate(todo: any, due_date: string | null) {
    if (this.is_local) return this.UpdateLocalTodo(todo.guid, { due_date });
    return this.EmitChanged(await this.apiService.put(`todos/${todo.guid}`, { due_date }));
  }

  // Saves just the checklist (used when items are ticked off from the list)
  async UpdateChecklist(guid: string, checklist: any[]) {
    if (this.is_local) return this.UpdateLocalTodo(guid, { checklist });
    return await this.apiService.put(`todos/${guid}`, { checklist });
  }

  // Number of open To Dos assigned to the current team member (nav bar badge)
  async GetMyOpenCount(): Promise<number> {
    const user = this.globalService.GetCurrentUser();
    if (!user) return 0;
    if (this.is_local) return this.ReadLocalTodos().filter(t => t.assigned_member_guid === user.guid && !this.IsCompleted(t)).length;
    const data: any = await this.apiService.get(`todos?assigned_to=${user.guid}&completed=0&count=1`);
    return data?.success && data.data?.[0] ? data.data[0].count : 0;
  }

  private EmitChanged(data: any) {
    if (data?.success) this.changed.next();
    return data;
  }

  async SetCompleted(todo: any, completed: boolean) {
    const user = this.globalService.GetCurrentUser();
    const body = completed
      ? { status: TaskStatus.completed, completed_date: dayjs().toISOString(), completed_by_guid: user.guid }
      : { status: TaskStatus.active, completed_date: null, completed_by_guid: null };
    const data: any = this.is_local
      ? this.UpdateLocalTodo(todo.guid, body)
      : await this.apiService.put(`todos/${todo.guid}`, body);
    if (data?.success) Object.assign(todo, body);
    this.EmitChanged(data);
    return data;
  }

  async DeleteTodo(todo: any) {
    if (this.is_local) {
      this.WriteLocalTodos(this.ReadLocalTodos().filter(t => t.guid !== todo.guid));
      return { success: true };
    }
    return this.EmitChanged(await this.apiService.delete(`todos/${todo.guid}`, null));
  }

  async GetTeamMembers(force_refresh = false): Promise<any[]> {
    if (this.team_members && !force_refresh) return this.team_members;
    const data: any = await this.apiService.get('team_members');
    if (data.success) this.team_members = data.data || [];
    // Without the team member list, still allow assigning To Do items to yourself
    if (!this.team_members?.length) {
      const user = this.globalService.GetCurrentUser();
      return user ? [{ guid: user.guid, display_name: user.display_name || user.name || user.email || 'Me' }] : [];
    }
    return this.team_members;
  }

  // Keeps communication types created from the tag picker in local mode
  AddLocalCommunicationTypes(types: any[]) {
    if (!this.is_local || !types?.length) return;
    const existing = this.ReadLocalCommunicationTypes();
    const known = new Set(existing.map(t => t.guid));
    const added = types.filter(t => t?.guid && !known.has(t.guid)).map(t => ({ guid: t.guid, title: t.title, colour: t.colour, description: t.description || '' }));
    if (added.length) this.WriteLocalCommunicationTypes([...existing, ...added]);
  }

  async GetCommunicationTypes(): Promise<any[]> {
    if (this.is_local) return this.ReadLocalCommunicationTypes();
    const data: any = await this.apiService.get(`tags/${TODO_COMMUNICATION_TYPE_TAGS}`);
    return data.success ? (data.data || []) : [];
  }

  // Resolves once the manager is closed so callers can reload the list
  async ManageCommunicationTypes(): Promise<void> {
    if (!this.is_local) {
      const modal = await this.modalController.create({
        component: TagsManageComponent,
        cssClass: 'tall-modal medium-modal secondary-modal',
        componentProps: {
          url_base: 'tags',
          url: TODO_COMMUNICATION_TYPE_TAGS,
          title: 'Manage Communication Types',
        },
      });
      await modal.present();
      await modal.onDidDismiss();
      return;
    }

    const types = this.ReadLocalCommunicationTypes();
    const alert = await this.alertController.create({
      header: 'Communication Types',
      message: 'Uncheck a type to remove it.',
      inputs: types.map(t => ({ type: 'checkbox' as const, label: t.title, value: t.guid, checked: true })),
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Add New', role: 'add' },
        { text: 'Save', role: 'save' },
      ],
    });
    await alert.present();
    const { role, data } = await alert.onDidDismiss();

    if (role === 'save') {
      const keep: string[] = data?.values || [];
      this.WriteLocalCommunicationTypes(types.filter(t => keep.includes(t.guid)));
    } else if (role === 'add') {
      const add_alert = await this.alertController.create({
        header: 'New Communication Type',
        inputs: [{ name: 'title', type: 'text', placeholder: 'e.g. Fax' }],
        buttons: [{ text: 'Cancel', role: 'cancel' }, { text: 'Add', role: 'add' }],
      });
      await add_alert.present();
      const added = await add_alert.onDidDismiss();
      const title = (added.data?.values?.title || '').trim();
      if (added.role === 'add' && title) {
        this.WriteLocalCommunicationTypes([...types, { guid: this.NewLocalGuid(), title, colour: '#90ee90' }]);
      }
      return this.ManageCommunicationTypes();
    }
  }

  // Converts between stored ISO strings and the value format of <input type="datetime-local">
  ToLocalInput(iso: string): string {
    return iso ? dayjs(iso).format('YYYY-MM-DDTHH:mm') : '';
  }

  FromLocalInput(value: string): string {
    return value ? dayjs(value).toISOString() : null;
  }

  // The API stores the checklist as a JSON string; the rest of the app works with an array
  private NormalizeResponse(data: any) {
    if (data?.success && Array.isArray(data.data)) {
      data.data.forEach((todo: any) => {
        let checklist = todo.checklist;
        if (typeof checklist === 'string') {
          try { checklist = JSON.parse(checklist); } catch (e) { checklist = []; }
        }
        todo.checklist = Array.isArray(checklist) ? checklist : [];
      });
    }
    return data;
  }

  private ToPayload(todo: any) {
    return {
      title: todo.title,
      description: todo.description,
      received_date: todo.received_date,
      // Stored like tags on other records: [{ guid, title, colour }]
      communication_types: (todo.communication_types || []).map(t => ({ guid: t.guid, title: t.title, colour: t.colour })),
      received_by_guid: todo.received_by_guid || null,
      // Optional internal link to one contact or company (never shown in the portal)
      client_guid: todo.client_guid || null,
      company_guid: todo.company_guid || null,
      linked_name: todo.linked_name || '',
      assigned_member_guid: todo.assigned_member_guid || null,
      due_date: todo.due_date,
      status: todo.status,
      completed_date: todo.completed_date,
      completed_by_guid: todo.completed_by_guid,
      // [{ guid, text, checked, checked_date, checked_by }]
      checklist: todo.checklist || [],
    };
  }

  // ── localStorage mode ─────────────────────────────────────────────────────

  private UpdateLocalTodo(guid: string, changes: any) {
    const todos = this.ReadLocalTodos();
    const index = todos.findIndex(t => t.guid === guid);
    if (index === -1) return { success: false, message: 'To Do not found' };
    todos[index] = { ...todos[index], ...changes };
    this.WriteLocalTodos(todos);
    return { success: true, data: [todos[index]] };
  }

  private ReadLocalTodos(): any[] {
    return this.ReadLocal(LOCAL_TODOS_KEY) || [];
  }

  private WriteLocalTodos(todos: any[]) {
    this.WriteLocal(LOCAL_TODOS_KEY, todos);
  }

  private ReadLocalCommunicationTypes(): any[] {
    const types = this.ReadLocal(LOCAL_COMMUNICATION_TYPES_KEY);
    if (types) return types;
    const defaults = DEFAULT_COMMUNICATION_TYPES.map(type => ({ guid: this.NewLocalGuid(), ...type }));
    this.WriteLocalCommunicationTypes(defaults);
    return defaults;
  }

  private WriteLocalCommunicationTypes(types: any[]) {
    this.WriteLocal(LOCAL_COMMUNICATION_TYPES_KEY, types);
  }

  private ReadLocal(key: string): any[] {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error(`Error reading ${key} from localStorage`, e);
      return null;
    }
  }

  private WriteLocal(key: string, value: any[]) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error(`Error writing ${key} to localStorage`, e);
    }
  }

  private NewLocalGuid(): string {
    return 'local-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
}
